import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useState, useEffect } from 'react';
import { useClientContext } from '@/contexts/ClientContext';

// Mapeamento de itens do menu para códigos de módulos
export const MODULE_MAPPING: Record<string, string[]> = {
  // Tickets
  'incidents': ['incidents'],
  'requests': ['requests'],
  'problems': ['problems'],
  'changes': ['changes'],
  'tickets': ['incidents', 'requests', 'problems', 'changes'],
  
  // Base de Conhecimento
  'knowledge': ['knowledge_base'],
  
  // Formulários
  'formularios': ['forms'],
  'formularios-lista': ['forms'],
  'planos-acao': ['forms'],
  
  // Relatórios
  'reports': ['reports'],
  
  // CMDB
  'cmdb': ['asset_management', 'software_management'],
  'ativos': ['asset_management'],
  'tipos-ativos': ['asset_management'],
  'softwares': ['software_management'],
  'contratos': ['asset_management'],
  'fabricantes': ['asset_management'],
  'fornecedores': ['asset_management'],
  'localizacao': ['asset_management'],
  'acmdb': ['asset_management'],
  
  // SLA
  'sla': ['sla_management'],
};

interface ClientModule {
  id: string;
  client_id: string;
  module_code: string;
  is_active: boolean;
  source: string;
}

export const useClientModules = () => {
  const { profile, user } = useAuth();
  const [profileClientId, setProfileClientId] = useState<string | null>(null);
  
  const isGlobalAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  
  // Obter cliente selecionado no ClientSwitcher
  const clientContext = useClientContext();
  const switcherClientId = clientContext?.currentClientId;
  const showAllClients = clientContext?.showAllClients ?? true;
  
  // Para admins: se tem cliente selecionado no switcher, usar esse; se "Todos", bypass
  // Para não-admins: usar clientId do perfil
  const shouldBypass = isGlobalAdmin && showAllClients;
  const effectiveClientId = isGlobalAdmin 
    ? (showAllClients ? null : switcherClientId)
    : profileClientId;
  
  // Buscar client_id do usuário (apenas para não-admins)
  useEffect(() => {
    const fetchClientId = async () => {
      if (!user?.id || isGlobalAdmin) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('id', user.id)
        .single();
      
      if (data?.client_id) {
        setProfileClientId(data.client_id);
      }
    };
    
    fetchClientId();
  }, [user?.id, isGlobalAdmin]);
  
  const { data: modules = [], isLoading, error, refetch } = useQuery({
    queryKey: ['client-modules', effectiveClientId],
    queryFn: async () => {
      if (!effectiveClientId) return [];
      
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('client_id', effectiveClientId)
        .eq('is_active', true);
      
      if (error) throw error;
      return data as ClientModule[];
    },
    enabled: !!effectiveClientId && !shouldBypass,
  });

  // Extrair apenas os códigos dos módulos ativos
  const activeCodes = modules.map(m => m.module_code);

  // Verificar se o cliente tem acesso a um módulo específico
  const hasModule = (moduleCode: string): boolean => {
    if (shouldBypass) return true;
    return activeCodes.includes(moduleCode);
  };

  // Verificar se um item do menu está habilitado
  const isMenuItemEnabled = (menuItemId: string): boolean => {
    if (shouldBypass) return true;
    
    // Dashboard sempre visível
    if (menuItemId === 'dashboard') return true;
    
    // Contatos e Configurações são verificados pelo SidebarNavigation via role
    if (menuItemId === 'contacts' || menuItemId === 'settings') return true;
    if (menuItemId === 'users' || menuItemId === 'groups' || menuItemId === 'profiles') return true;
    if (menuItemId === 'clients' || menuItemId === 'categoria') return true;
    
    const requiredModules = MODULE_MAPPING[menuItemId];
    if (!requiredModules) return true; // Itens sem mapeamento são visíveis
    
    // Verificar se pelo menos um dos módulos necessários está ativo
    return requiredModules.some(code => hasModule(code));
  };

  // Obter lista de módulos ativos para exibição
  const getActiveModuleCodes = (): string[] => {
    if (shouldBypass) {
      return Object.values(MODULE_MAPPING).flat().filter((v, i, a) => a.indexOf(v) === i);
    }
    return activeCodes;
  };

  return { 
    modules,
    activeCodes,
    isLoading, 
    error,
    hasModule,
    isMenuItemEnabled,
    getActiveModuleCodes,
    isGlobalAdmin,
    refetch,
  };
};
